import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';
import { Asociado } from '../../../api';
import { mensajeErrorAccion, mensajeErrorCarga } from '../../core/http-errors';
import { AsociadosListadoService } from '../../services/asociados-listado.service';
import { CatalogosHistoricoService, OpcionEjercicio, TIPOS_ASOCIACION, textoTipoAsociacion } from '../../services/catalogos-historico.service';
import { EntradaLote, FilaLote, HistoricoApiService, PlanLote } from '../../services/historico-api.service';
import { NavegacionService } from '../../services/navegacion.service';
import { PermisosService } from '../../services/permisos.service';
import { AlertService } from '../alert/alert.service';
import { AsociadoBuscadorComponent } from '../asociado-buscador/asociado-buscador.component';
import { AlertButtonType } from '../dialog-alert/AlertButtonType.enum';
import { DialogAlertService } from '../dialog-alert/dialog-alert.service';
import { OpcionBusqueda, SelectorBusquedaComponent } from '../selector-busqueda/selector-busqueda.component';
import { SelectorEjerciciosComponent } from '../selector-ejercicios/selector-ejercicios.component';

export const ETIQUETA_FILA: Record<string, { texto: string; clase: string }> = {
  crear: { texto: 'Se creará', clase: 'text-bg-success' },
  creada: { texto: 'Creada', clase: 'text-bg-success' },
  actualizar: { texto: 'Se actualizará', clase: 'text-bg-warning' },
  actualizada: { texto: 'Actualizada', clase: 'text-bg-info' },
  existente: { texto: 'Ya existe', clase: 'text-bg-secondary' },
  conflicto: { texto: 'Conflicto', clase: 'text-bg-danger' }
};

/**
 * Asignación de una relación (asociado + asociación + cargo) a VARIOS ejercicios (P1.3 Wave 5). Flujo: elegir asociado, asociación,
 * cargo y ejercicios → vista previa (la API calcula qué se crearía, qué ya existe y qué entra en conflicto, y no escribe nada) →
 * confirmar → resultado. Cualquier cambio en la selección invalida la vista previa (la API además exige la firma de esa vista previa:
 * si los datos cambiaron en la base, la confirmación se rechaza con 409). Solo crea relaciones nuevas; nunca modifica ni borra.
 */
import { PageHeaderComponent } from '../ui/page-header.component';

@Component({
  selector: 'app-historico-lote',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, AsociadoBuscadorComponent, SelectorBusquedaComponent, SelectorEjerciciosComponent, PageHeaderComponent],
  templateUrl: './historico-lote.component.html'
})
export class HistoricoLoteComponent implements OnInit, OnDestroy {
  readonly ETIQUETA = ETIQUETA_FILA;
  readonly GRUPOS_ASOCIACION = TIPOS_ASOCIACION.map(t => ({ id: t.id, texto: t.texto }));
  readonly GRUPOS_CARGO = [{ id: 0, texto: 'Adultos' }, { id: 1, texto: 'Infantiles' }];

  readonly texto = new FormControl<string | null>('');
  asociado: Asociado | null = null;
  recuperandoAsociado = false;
  errorAsociado = '';

  asociaciones: OpcionBusqueda[] = [];
  cargos: OpcionBusqueda[] = [];
  ejercicios: OpcionEjercicio[] = [];
  cargandoCatalogos = true;
  errorCatalogos = '';

  asociacionId: number | null = null;
  cargoId: number | null = null;
  seleccion: number[] = [];

  plan: PlanLote | null = null;
  resultado: PlanLote | null = null;
  previsualizando = false;
  aplicando = false;
  error = '';
  /** La vista previa que había dejó de valer porque cambió la selección. */
  previaInvalidada = false;

  private readonly suscripciones = new Subscription();

  constructor(
    private readonly api: HistoricoApiService,
    private readonly catalogos: CatalogosHistoricoService,
    private readonly asociadosApi: AsociadosListadoService,
    private readonly dialogo: DialogAlertService,
    private readonly alertas: AlertService,
    private readonly permisos: PermisosService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly nav: NavegacionService
  ) {}

  get puedeEscribir(): boolean { return this.permisos.tieneGlobal('historico:escribir'); }
  get ocupado(): boolean { return this.previsualizando || this.aplicando; }
  get entradaCompleta(): boolean { return !!this.asociado && this.asociacionId !== null && this.cargoId !== null && this.seleccion.length > 0; }
  get puedePrevisualizar(): boolean { return this.puedeEscribir && this.entradaCompleta && !this.ocupado && !this.cargandoCatalogos; }
  get puedeConfirmar(): boolean { return !!this.plan?.firma && ((this.plan.resumen.crear ?? 0) + (this.plan.resumen.actualizar ?? 0)) > 0 && !this.ocupado; }
  textoTipo = textoTipoAsociacion;

  ngOnInit(): void {
    this.cargarCatalogos();
    const id = Number(this.route.snapshot.queryParamMap.get('asociado'));
    if (Number.isInteger(id) && id > 0) this.recuperarAsociado(id);
  }

  ngOnDestroy(): void { this.suscripciones.unsubscribe(); }

  cargarCatalogos(): void {
    this.cargandoCatalogos = true;
    this.errorCatalogos = '';
    this.suscripciones.add(forkJoin([this.catalogos.asociacionesOrdenadas(), this.catalogos.cargosOrdenados(), this.catalogos.ejerciciosRecientes()]).subscribe({
      next: ([asociaciones, cargos, ejercicios]) => {
        this.asociaciones = asociaciones.map(a => ({ id: a.id, texto: a.nombre, detalle: textoTipoAsociacion(a.tipo), grupo: a.tipo, inactiva: !a.activa }));
        this.cargos = cargos.map(c => ({ id: c.id, texto: c.nombre + (c.esInfantil ? ' (infantil)' : ''), grupo: c.esInfantil ? 1 : 0, inactiva: !c.activo }));
        this.ejercicios = ejercicios;
        this.cargandoCatalogos = false;
      },
      error: (err: HttpErrorResponse) => {
        this.errorCatalogos = mensajeErrorCarga(err, 'los catálogos');
        this.cargandoCatalogos = false;
      }
    }));
  }

  private recuperarAsociado(id: number): void {
    this.recuperandoAsociado = true;
    this.errorAsociado = '';
    this.suscripciones.add(this.asociadosApi.obtenerPorId(id).subscribe({
      next: res => {
        this.recuperandoAsociado = false;
        const encontrado = res?.asociados?.[0];
        if (encontrado) this.fijarAsociado(encontrado); else this.errorAsociado = 'No se ha encontrado el asociado.';
      },
      error: (err: HttpErrorResponse) => {
        this.recuperandoAsociado = false;
        this.errorAsociado = mensajeErrorCarga(err, 'el asociado');
      }
    }));
  }

  fijarAsociado(asociado: Asociado): void {
    this.asociado = asociado;
    this.invalidarPrevia();
  }

  cambiarAsociado(): void {
    this.asociado = null;
    this.texto.setValue('');
    this.invalidarPrevia();
  }

  elegirAsociacion(id: number | null): void { this.asociacionId = id; this.invalidarPrevia(); }
  elegirCargo(id: number | null): void { this.cargoId = id; this.invalidarPrevia(); }
  cambiarEjercicios(ids: number[]): void { this.seleccion = ids; this.invalidarPrevia(); }

  /** Cualquier cambio de entrada deja sin valor la vista previa y el resultado anteriores. */
  private invalidarPrevia(): void {
    if (this.plan) this.previaInvalidada = true;
    this.plan = null;
    this.resultado = null;
    this.error = '';
  }

  entrada(): EntradaLote {
    return { idAsociado: this.asociado!.id, idAsociacion: this.asociacionId!, idCargo: this.cargoId!, ejercicios: this.seleccion.slice().sort((a, b) => a - b) };
  }

  previsualizar(): void {
    if (!this.puedePrevisualizar) return;
    this.previsualizando = true;
    this.previaInvalidada = false;
    this.error = '';
    this.resultado = null;
    this.suscripciones.add(this.api.vistaPreviaLote(this.entrada()).subscribe({
      next: res => { this.plan = res.lote; this.previsualizando = false; },
      error: (err: HttpErrorResponse) => {
        this.plan = null;
        this.previsualizando = false;
        this.error = mensajeErrorAccion(err, 'No se ha podido calcular la vista previa.');
      }
    }));
  }

  /** Texto de la confirmación: proporcionado al riesgo (si incluye el ejercicio vigente se advierte de que concede permisos al momento). */
  textoConfirmacion(): string {
    const plan = this.plan!;
    const crear = plan.filas.filter(f => f.estado === 'crear' || f.estado === 'actualizar');
    const actualizar = plan.filas.filter(f => f.estado === 'actualizar');
    const anios = crear.map(f => f.anyoEjercicio).sort((a, b) => a - b).join(', ');
    const omitidas = plan.resumen.existentes + plan.resumen.conflictos;
    const partes = [
      `Vas a registrar «${plan.cargo.nombre}» de «${plan.asociado.nombre} ${plan.asociado.apellidos}» en «${plan.asociacion.nombre}» para ${crear.length} ejercicio(s): ${anios}.`
    ];
    const altas = crear.filter(f => f.tipoMovimiento === 'alta').length;
    const cambios = crear.filter(f => f.tipoMovimiento === 'cambio').length;
    partes.push('Se registrarán ' + altas + ' alta(s) (la persona se incorpora a la asociación en ese ejercicio) y ' + cambios + ' cambio(s) de cargo (ya pertenece a la asociación en ese ejercicio).');
    if (actualizar.length) partes.push('Se actualizarán ' + actualizar.length + ' relación(es) que constan como baja (reingreso o cargo recuperado): la baja anterior —su tipo, estado y fecha— queda registrada en la auditoría.');
    if (omitidas) partes.push(`Se omiten ${plan.resumen.existentes} ya existente(s) y ${plan.resumen.conflictos} con conflicto: no se modifican.`);
    if (crear.some(f => f.ejercicioVigente === 1)) partes.push('ATENCIÓN: incluye el ejercicio vigente. El cargo pasará a estar vigente de inmediato y puede conceder permisos.');
    partes.push('No se borra nada ni se tocan las demás relaciones, asociaciones o ejercicios. La operación queda registrada en la auditoría. ¿Quieres continuar?');
    return partes.join('\n\n');
  }

  confirmar(): void {
    if (!this.puedeConfirmar) return;
    this.suscripciones.add(this.dialogo.openDialogAlert({
      title: 'Asignar a varios ejercicios',
      content: this.textoConfirmacion(),
      buttonsAlert: [AlertButtonType.Cancelar, AlertButtonType.Aceptar]
    }).afterClosed().subscribe(respuesta => { if (respuesta === AlertButtonType.Aceptar) this.aplicar(); }));
  }

  private aplicar(): void {
    if (this.ocupado || !this.plan?.firma) return;
    this.aplicando = true;
    this.error = '';
    this.suscripciones.add(this.api.aplicarLote(this.entrada(), this.plan.firma).subscribe({
      next: res => {
        this.aplicando = false;
        this.resultado = res.lote;
        this.plan = null;
        this.alertas.success(`Se han creado ${res.lote.resumen.creadas ?? 0} y actualizado ${res.lote.resumen.actualizadas ?? 0} relación(es) en el histórico.`, 5000);
      },
      error: (err: HttpErrorResponse) => {
        this.aplicando = false;
        // 409: la vista previa ya no vale (otro usuario cambió los datos) o no hay nada que crear: se obliga a generarla de nuevo
        if (err?.status === 409) { this.plan = null; this.previaInvalidada = true; }
        this.error = mensajeErrorAccion(err, 'No se ha podido completar la asignación.');
      }
    }));
  }

  nueva(): void {
    this.resultado = null;
    this.plan = null;
    this.seleccion = [];
    this.error = '';
    this.previaInvalidada = false;
  }

  verHistorico(): void {
    if (this.asociado) this.router.navigate(['/historico'], { queryParams: { asociado: this.asociado.id } });
  }

  volver(): void { this.nav.volver('/historico'); }

  filas(plan: PlanLote | null): FilaLote[] { return plan?.filas ?? []; }
  nombreCompleto(a: Asociado): string { return [a.apellidos, a.nombre].filter(Boolean).join(', ') || '—'; }
}
